import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { AttentionLevel, Task } from '@/src/renderer/lib/db/models'
import { TaskAttentionBadge } from '../TaskAttentionBadge'
import { TaskStartDateBadge } from '../TaskStartDateBadge'
import type { StartDateLabelMode } from '../TaskStartDateBadge'
import { TaskDropdown } from '../TaskDropdown'
import { AttentionDropdownContent } from '../AttentionDropdown'
import { WhenDropdownContent } from '../WhenDropdown'
import type { StartValue } from '../WhenDropdown'

interface CollapsedTitleIconsProps {
  isExpanded: boolean
  selected: boolean
  effectiveAttention: AttentionLevel | null
  effectiveStartDate: Date | null
  startDiff: number | null
  collapsedAttentionOpen: boolean
  setCollapsedAttentionOpen: (open: boolean) => void
  collapsedStartOpen: boolean
  setCollapsedStartOpen: (open: boolean) => void
  collapsedStartValue: StartValue | null
  locale: string
  setAttention: (value: AttentionLevel | null) => void
  setStart: (value: StartValue | null) => void
  saveCollapsedToDb: (updates: Partial<Omit<Task, 'id'>>) => void
  selectOnDropdownOpen: (open: boolean) => void
  switchCollapsedDropdown: (target: 'attention' | 'start' | 'duration' | 'deadline' | 'tags', tagId?: string) => void
  playSoftRemove: () => void
  startDateLabelMode?: StartDateLabelMode
}

export const CollapsedTitleIcons: React.FC<CollapsedTitleIconsProps> = ({
  isExpanded, selected,
  effectiveAttention, effectiveStartDate, startDiff,
  collapsedAttentionOpen, setCollapsedAttentionOpen,
  collapsedStartOpen, setCollapsedStartOpen,
  collapsedStartValue, locale,
  setAttention, setStart, saveCollapsedToDb,
  selectOnDropdownOpen, switchCollapsedDropdown, playSoftRemove,
  startDateLabelMode,
}) => (
  <AnimatePresence initial={false}>
    {!isExpanded && effectiveAttention && (
      <motion.span
        key="attention-icon"
        className="task-entry__title-icon"
        initial={{ width: 0, opacity: 0, marginRight: 0 }}
        animate={{ width: 20, opacity: 1, marginRight: 8 }}
        exit={{ width: 0, opacity: 0, marginRight: 0, scale: 0.7 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => switchCollapsedDropdown('attention')}
      >
        <TaskDropdown
          open={collapsedAttentionOpen}
          onOpenChange={(open) => { setCollapsedAttentionOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="start"
          alignOffset={-16}
          contentStyle={{ width: 220 }}
          trigger={<button className="task-entry__title-icon-btn task-entry__title-icon-btn--icon-only" type="button"><TaskAttentionBadge level={effectiveAttention} /></button>}
        >
          <AttentionDropdownContent
            value={effectiveAttention}
            onChange={(value) => { setAttention(value); saveCollapsedToDb({ attention: value }) }}
            onClose={() => setCollapsedAttentionOpen(false)}
            onRemove={() => { playSoftRemove(); setAttention(null); saveCollapsedToDb({ attention: null }); setCollapsedAttentionOpen(false) }}
            variant="badge"
          />
        </TaskDropdown>
      </motion.span>
    )}
    {!isExpanded && effectiveStartDate && startDiff !== null && (
      <motion.span
        key="start-day-icon"
        className="task-entry__title-icon"
        initial={{ width: 0, opacity: 0, marginRight: 0 }}
        animate={{ width: 'auto', opacity: 1, marginRight: 8 }}
        exit={{ width: 0, opacity: 0, marginRight: 0, scale: 0.7 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => switchCollapsedDropdown('start')}
      >
        <TaskDropdown
          open={collapsedStartOpen}
          onOpenChange={(open) => { setCollapsedStartOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="start"
          alignOffset={-16}
          trigger={<button className={`task-entry__title-icon-btn${startDiff === 0 || startDiff === 1 ? ' task-entry__title-icon-btn--icon-only' : ''}${selected ? ' task-entry__title-icon-btn--selected' : ''}`} type="button"><TaskStartDateBadge date={effectiveStartDate} locale={locale} period={collapsedStartValue?.period ?? null} labelMode={startDateLabelMode} /></button>}
        >
          <WhenDropdownContent
            value={collapsedStartValue}
            onChange={(value) => {
              setStart(value)
              saveCollapsedToDb({ startDate: value?.date ?? null, startTime: value?.time ?? null, startPeriod: value?.period ?? null })
            }}
            onClose={() => setCollapsedStartOpen(false)}
            onRemove={() => { playSoftRemove(); setStart(null); saveCollapsedToDb({ startDate: null, startTime: null, startPeriod: null }); setCollapsedStartOpen(false) }}
            variant="badge"
          />
        </TaskDropdown>
      </motion.span>
    )}
  </AnimatePresence>
)
